import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type {
  Element,
  GroupElement as GroupElementType,
  ImageElement as ImageElementType,
  LineElement as LineElementType,
  TableElement as TableElementType,
  TextElement as TextElementType,
  ElementPatch,
  Page,
} from '@/components/canvas/document/publicTypes';
import { TABLE_MIN_COL_WIDTH, TABLE_MIN_ROW_HEIGHT } from '@/components/canvas/shared/constants/legacyConfig';
import { resolvePageGuideSettings } from '@/components/canvas/page/guideSettings';
import { computeGridGuideLines } from '@/components/canvas/workspace/overlays/gridGuides';
import { isWithinTextEditChrome } from '@/components/canvas/elements/text/overlays/textEditChrome';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import { getLineChromeRect, getLineEndOffset } from '@/components/canvas/elements/shape/line/lineElementPatch';
import { getMergedRangeContaining } from '@/components/canvas/elements/table/utils/tableUtilsIndex';
import {
  useElementDrag,
  type ElementDragMovePayload,
  useResize,
  useLineHandle,
  useRadiusDrag,
  useRotate,
  useGroupResize,
  useMultiSelectionResize,
  buildDocumentSnapshots,
  unionBoundsFromSnapshots,
  type ResizeHandle,
  type GetMinSize,
} from '@/components/canvas/transform';
import type { MarqueeScope } from './marqueeSelectUtils';
import {
  pageSurfaceWorldBounds,
  unionBoundsFromPageElements,
  unionBoundsFromRects,
} from '@/components/canvas/selection/unionBounds';
import { textEditingState } from '@/components/canvas/elements/text/controllers/textSelection';
import { getTextMinSize } from '@/components/canvas/elements/text/utils/textMeasure';
import { getTextLayoutFromElement } from '@/components/canvas/elements/text/model/textStyles';
import { getElementAbsolutePos } from '@/components/canvas/workspace/model/getElementAbsolutePos';
import { isHitInCurrentSelection } from '@/components/canvas/workspace/model/contextMenuSelection';
import type { ComputedGridGuides } from '@/components/canvas/workspace/overlays/gridGuides';
import type { WorkspacePageLayoutMode } from '@/components/canvas/workspace/model/workspacePageLayoutMode';
import {
  pageLocalBoundsToWorld,
  resolvePageCrossDropTargetPage,
  resolveWorkspaceDropTargetPage,
  shouldMovePageElementsToWorkspace,
  unionBoundsFromDrag,
} from '@/components/canvas/workspace/model/elementOwnership';

/** 指针移动超过该距离（世界坐标）后才判定拖入页 */
const WORKSPACE_DROP_POINTER_MOVE_THRESHOLD = 0.5;

export type CanvasContextMenuTarget =
  | { type: 'workspace'; point: { x: number; y: number } }
  | { type: 'page'; pageId: string; point: { x: number; y: number } }
  | { type: 'element'; element: Element };

function gridGuidesForPage(page: Page): ComputedGridGuides {
  const guideSettings = resolvePageGuideSettings(page);
  if (!guideSettings.enabled || guideSettings.system !== 'grid') {
    return { horizontal: [], vertical: [] };
  }
  return computeGridGuideLines(page.size.width, page.size.height, guideSettings.grid);
}

import { usePageResize } from '@/components/canvas/page/controllers/usePageResize';
import type { PageResizePreview } from '@/components/canvas/page/controllers/usePageResize';

export interface UseWorkspaceInteractionOptions {
  elements: Element[];
  gridGuides: ComputedGridGuides;
  background: { type: string; value?: string | null };
  pageRef: React.RefObject<HTMLDivElement | null>;
  stageHostRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
  panX: number;
  panY: number;
  selectedElementIds: string[];
  selectElement: (id: string, addToSelection: boolean) => void;
  selectElements: (ids: string[], scope?: MarqueeScope | null) => void;
  clearSelection: () => void;
  deactivateCurrentPage: () => void;
  editingTextId: string | null;
  setEditingTextId: (id: string | null) => void;
  editingGroupId: string | null;
  setEditingGroupId: (id: string | null) => void;
  updateElement: (id: string, patch: ElementPatch) => void;
  updateElementSilent: (id: string, patch: ElementPatch) => void;
  pushHistory: () => void;
  cancelPendingHistory: () => void;
  setElementPositions: (updates: { id: string; x?: number; y?: number }[]) => void;
  setWorkspaceElementPositions: (updates: { id: string; x?: number; y?: number }[]) => void;
  commitElementDrag: (payload: {
    source: 'page' | 'workspace';
    sourcePageId?: string;
    target: 'page' | 'workspace';
    targetPageId?: string;
    positions: { id: string; x: number; y: number }[];
  }) => void;
  commitGroupResize: (
    groupId: string,
    groupPatch: { x: number; y: number; width: number; height: number },
    childrenPatches: { id: string; patch: ElementPatch }[],
  ) => void;
  penToolActive: boolean;
  freehandToolActive: boolean;
  eraserToolActive: boolean;
  isPanModeActive: boolean;
  isMovingBgImage: boolean;
  setIsMovingBgImage: (v: boolean) => void;
  handleViewportMouseUp: (e: React.MouseEvent) => void;
  selectedTableCells: { elementId: string; r0: number; c0: number; r1: number; c1: number } | null;
  setSelectedTableCells: (
    payload: { elementId: string; r0: number; c0: number; r1: number; c1: number } | null,
  ) => void;
  editingTableCellId: string | null;
  setEditingTableCellId: (id: string | null) => void;
  selectedTableColumn: { elementId: string; col: number } | null;
  selectedTableRow: { elementId: string; row: number } | null;
  effectiveTextDrawMode: boolean;
  effectiveShapeDrawMode: boolean;
  effectiveTableDrawMode: boolean;
  isMarqueeActive: boolean;
  marquee: { x: number; y: number; width: number; height: number } | null;
  isTextDrawing: boolean;
  textDrawRect: { x: number; y: number; width: number; height: number } | null;
  isShapeDrawing: boolean;
  shapeDrawPreview: import('@/components/canvas/elements/shape/controllers/shapeDrawGeometry').ShapeDrawPreview | null;
  isTableDrawing: boolean;
  tableDrawRect: import('@/components/canvas/elements/table/controllers/useTableDraw').TableDrawRect | null;
  tableDrawPending: import('@/components/canvas/elements/table/controllers/useTableDraw').TableDrawPending | null;
  setTableDrawRows: (rows: number) => void;
  setTableDrawCols: (cols: number) => void;
  confirmTableDraw: () => void;
  cancelTableDraw: () => void;
  panCursor: string | null;
  handleCanvasMouseDown: (e: React.MouseEvent) => void;
  startBgImageDrag: (e: React.MouseEvent) => void;
  isPresentationMode: boolean;
  isReadonlyMode: boolean;
  isPageSelected: boolean;
  selectPageFrame: () => void;
  applyPageFrameResizeSilent: (payload: {
    width: number;
    height: number;
    workspaceX: number;
    workspaceY: number;
    localOffsetX: number;
    localOffsetY: number;
    elementStarts: { id: string; x: number; y: number }[];
  }) => void;
  commitPageFrameResize: (payload: {
    width: number;
    height: number;
    workspaceX: number;
    workspaceY: number;
    localOffsetX: number;
    localOffsetY: number;
    elementStarts: { id: string; x: number; y: number }[];
  }) => void;
  pageSize: { width: number; height: number };
  getPage: () => Page | null;
  onPageFrameDragStart: (e: React.MouseEvent) => void;
  pages: Page[];
  workspaceElements: Element[];
  currentPageId: string;
  setCurrentPageId: (id: string) => void;
  bringPageToFront: (pageId: string) => void;
  selectedPageIds: string[];
  selectionScope: MarqueeScope | null;
  updateElementSilentInDocument: (id: string, patch: ElementPatch) => void;
  commitMultiSelectionResize: (patches: { id: string; patch: ElementPatch }[]) => void;
  draggingPageId: string | null;
  workspacePageLayoutMode: WorkspacePageLayoutMode;
}

import type { BlockInteractionApi } from '@/components/canvas/blocks/useBlockInteraction';

export type WorkspaceInteractionApi = ReturnType<typeof useWorkspaceInteraction> &
  BlockInteractionApi & {
    selectedBlockIds: string[];
    hoveredBlockId: string | null;
    setHoveredBlockId: (id: string | null) => void;
    pageBlocks: import('@/components/canvas/blocks/types').Block[];
    blockDragOffset: { dx: number; dy: number };
    blockResizePreview: { x: number; y: number; width: number; height: number } | null;
    blockResizeState: { id: string } | null;
    handleBlockHitPointerDown: (e: React.PointerEvent, block: import('@/components/canvas/blocks/types').Block) => void;
  };

export function useWorkspaceInteraction(options: UseWorkspaceInteractionOptions) {
  const {
    elements,
    gridGuides,
    background,
    pageRef,
    stageHostRef,
    zoom,
    panX,
    panY,
    selectedElementIds,
    selectElement,
    selectElements,
    clearSelection,
    deactivateCurrentPage,
    editingTextId,
    setEditingTextId,
    editingGroupId,
    setEditingGroupId,
    updateElement,
    updateElementSilent,
    pushHistory,
    cancelPendingHistory,
    setElementPositions,
    setWorkspaceElementPositions,
    commitElementDrag,
    commitGroupResize,
    penToolActive,
    freehandToolActive,
    eraserToolActive,
    isPanModeActive,
    isMovingBgImage,
    setIsMovingBgImage,
    handleViewportMouseUp,
    selectedTableCells,
    setSelectedTableCells,
    editingTableCellId,
    setEditingTableCellId,
    selectedTableColumn,
    selectedTableRow,
    effectiveTextDrawMode,
    effectiveShapeDrawMode,
    effectiveTableDrawMode,
    isMarqueeActive,
    marquee,
    isTextDrawing,
    textDrawRect,
    isShapeDrawing,
    shapeDrawPreview,
    isTableDrawing,
    tableDrawRect,
    tableDrawPending,
    setTableDrawRows,
    setTableDrawCols,
    confirmTableDraw,
    cancelTableDraw,
    panCursor,
    handleCanvasMouseDown,
    startBgImageDrag,
    isPresentationMode,
    isReadonlyMode,
    isPageSelected,
    selectPageFrame,
    applyPageFrameResizeSilent,
    commitPageFrameResize,
    pageSize,
    getPage,
    onPageFrameDragStart,
    pages,
    workspaceElements,
    currentPageId,
    setCurrentPageId,
    bringPageToFront,
    selectedPageIds,
    selectionScope,
    updateElementSilentInDocument,
    commitMultiSelectionResize,
    draggingPageId,
    workspacePageLayoutMode,
  } = options;

  const [ownershipDropPageId, setOwnershipDropPageId] = useState<string | null>(null);
  const ownershipDropPageIdRef = useRef<string | null>(null);
  ownershipDropPageIdRef.current = ownershipDropPageId;
  /** 拖拽开始前激活的页；落页预览临时切换 currentPageId 后用于拖离/取消时恢复 */
  const currentPageIdBeforeDragRef = useRef<string | null>(null);
  const hadElementDragRef = useRef(false);
  const ownershipDropTargetCommittedRef = useRef<string | null>(null);
  /** 页内元素拖出：页内面积 <50% 时在工作区预览、页内隐藏 */
  const [pageDragEscapeToWorkspace, setPageDragEscapeToWorkspace] = useState(false);
  const hideSelectionChrome = isReadonlyMode || !!draggingPageId;

  const [editingImagePosition, setEditingImagePosition] = useState<{
    id: string;
    startClientX: number;
    startClientY: number;
    startObjectPositionX: number;
    startObjectPositionY: number;
  } | null>(null);

  const pageSnapRef = useRef<import('@/components/canvas/transform/useElementDrag').PageSnapContext>({
    elements: [],
    blocks: [],
    guides: { horizontal: [], vertical: [] },
    pageWidth: pageSize.width,
    pageHeight: pageSize.height,
    zoom,
  });
  const visibleElements = elements.filter((el) => el.interaction.visible !== false);
  const rootElements = visibleElements.filter((el) => !el.layout.parentId);

  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const workspaceElementsRef = useRef(workspaceElements);
  workspaceElementsRef.current = workspaceElements;
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const currentPageIdRef = useRef(currentPageId);
  currentPageIdRef.current = currentPageId;
  const workspacePageLayoutModeRef = useRef(workspacePageLayoutMode);
  workspacePageLayoutModeRef.current = workspacePageLayoutMode;
  const draggingPageIdRef = useRef(draggingPageId);
  draggingPageIdRef.current = draggingPageId;

  const restorePageBeforeDrag = useCallback(() => {
    const saved = currentPageIdBeforeDragRef.current;
    if (saved && saved !== currentPageIdRef.current) {
      setCurrentPageId(saved);
    }
  }, [setCurrentPageId]);

  /** 落页目标变化时才更新 state / 激活页，避免每帧 restore 导致选框闪烁 */
  const applyOwnershipDropTarget = useCallback(
    (nextDropId: string | null) => {
      const prev = ownershipDropTargetCommittedRef.current;
      if (prev !== nextDropId) {
        ownershipDropTargetCommittedRef.current = nextDropId;
        setOwnershipDropPageId(nextDropId);
      }
      if (nextDropId) {
        // 单页叠放：拖拽中勿切换激活页，否则 sortPagesForRender 顶层变化导致落页识别紊乱
        if (
          workspacePageLayoutModeRef.current !== 'single' &&
          nextDropId !== currentPageIdRef.current
        ) {
          setCurrentPageId(nextDropId);
          bringPageToFront(nextDropId);
        }
      } else if (prev != null && workspacePageLayoutModeRef.current !== 'single') {
        restorePageBeforeDrag();
      }
    },
    [setCurrentPageId, bringPageToFront, restorePageBeforeDrag],
  );

  const clearOwnershipDropTarget = useCallback(() => {
    applyOwnershipDropTarget(null);
  }, [applyOwnershipDropTarget]);

  // ── 组内编辑时的坐标变换：子元素坐标是相对于组的原点，resize/rotate hook 传入
  //    的是画布绝对坐标，写回时需减去组偏移。使用 ref 实现稳定函数引用。────────────
  const updateElementRef = useRef(updateElement);
  updateElementRef.current = updateElement;
  const updateElementSilentRef = useRef(updateElementSilent);
  updateElementSilentRef.current = updateElementSilent;
  /** 当前编辑组的信息（每帧更新，x/y 为画布绝对坐标） */
  const editingGroupDataRef = useRef<{ x: number; y: number; childIds: string[] } | null>(null);
  {
    // 在渲染阶段同步更新 ref（不触发重渲染）
    const grp = editingGroupId
      ? (elements.find((e) => e.id === editingGroupId) as import('@/components/canvas/document/publicTypes').GroupElement | undefined)
      : undefined;
    if (grp) {
      // 对嵌套组必须用画布绝对坐标，才能在 updateElementWrapped 中正确还原子元素坐标
      const absPos = getElementAbsolutePos(editingGroupId!, elements);
      editingGroupDataRef.current = { x: absPos.x, y: absPos.y, childIds: grp.data.children ?? [] };
    } else {
      editingGroupDataRef.current = null;
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateElementWrapped = useCallback((id: string, patch: import('@/components/canvas/document/publicTypes').ElementPatch) => {
    const adj = editingGroupDataRef.current;
    if (adj?.childIds.includes(id)) {
      const p = { ...patch };
      if (typeof p.layout?.x === 'number') p.layout = { ...p.layout, x: p.layout.x - adj.x };
      if (typeof p.layout?.y === 'number') p.layout = { ...p.layout, y: p.layout.y - adj.y };
      return updateElementRef.current(id, p);
    }
    return updateElementRef.current(id, patch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateElementSilentWrapped = useCallback((id: string, patch: import('@/components/canvas/document/publicTypes').ElementPatch) => {
    const adj = editingGroupDataRef.current;
    if (adj?.childIds.includes(id)) {
      const p = { ...patch };
      if (typeof p.layout?.x === 'number') p.layout = { ...p.layout, x: p.layout.x - adj.x };
      if (typeof p.layout?.y === 'number') p.layout = { ...p.layout, y: p.layout.y - adj.y };
      return updateElementSilentRef.current(id, p);
    }
    return updateElementSilentRef.current(id, patch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleElementDragMove = useCallback(
    (payload: ElementDragMovePayload) => {
      const pagesLive = pagesRef.current;

      if (payload.source === 'page' && payload.sourcePageId) {
        const page = pagesLive.find((p) => p.id === payload.sourcePageId);
        const sourceElements = page?.elements ?? [];
        if (!page) {
          setPageDragEscapeToWorkspace(false);
          clearOwnershipDropTarget();
          return;
        }
        const bounds = unionBoundsFromDrag(
          payload.startPositions,
          sourceElements,
          payload.dx,
          payload.dy,
        );
        if (!bounds) {
          setPageDragEscapeToWorkspace(false);
          clearOwnershipDropTarget();
          return;
        }

        const worldBounds = pageLocalBoundsToWorld(bounds, page);
        const crossPageDropId = resolvePageCrossDropTargetPage(
          workspacePageLayoutModeRef.current,
          pagesLive,
          worldBounds,
          payload.sourcePageId,
        );
        if (crossPageDropId) {
          setPageDragEscapeToWorkspace(false);
          applyOwnershipDropTarget(crossPageDropId);
          return;
        }

        const escape = shouldMovePageElementsToWorkspace(page, bounds);
        setPageDragEscapeToWorkspace(escape);
        if (escape) {
          clearOwnershipDropTarget();
          return;
        }

        setPageDragEscapeToWorkspace(false);
        clearOwnershipDropTarget();
        return;
      }
      setPageDragEscapeToWorkspace(false);
      if (payload.source !== 'workspace') {
        clearOwnershipDropTarget();
        return;
      }
      const pointerMoved =
        Math.hypot(payload.rawDx, payload.rawDy) >= WORKSPACE_DROP_POINTER_MOVE_THRESHOLD;
      if (!pointerMoved) {
        clearOwnershipDropTarget();
        return;
      }
      const bounds = unionBoundsFromDrag(
        payload.startPositions,
        workspaceElements,
        payload.dx,
        payload.dy,
      );
      const dropId = bounds
        ? resolveWorkspaceDropTargetPage(
            workspacePageLayoutModeRef.current,
            pagesLive,
            currentPageIdRef.current,
            bounds,
            draggingPageIdRef.current,
          )
        : null;
      const nextDropId = pointerMoved && dropId ? dropId : null;
      applyOwnershipDropTarget(nextDropId);
    },
    [workspaceElements, applyOwnershipDropTarget, clearOwnershipDropTarget],
  );

  const handleElementDragEnd = useCallback(
    (payload: ElementDragMovePayload) => {
      /** 松手瞬间是否仍处于「落页高亮」状态；避免仅用包围盒误判落页导致从 workspaceElements 被移走 */
      const dropPageIdAtRelease = ownershipDropPageIdRef.current;
      ownershipDropTargetCommittedRef.current = null;
      setOwnershipDropPageId(null);
      setPageDragEscapeToWorkspace(false);
      const positions = payload.startPositions.map(({ id, x, y }) => ({
        id,
        x: Math.round(x + payload.dx),
        y: Math.round(y + payload.dy),
      }));
      const movedIds = positions.map((p) => p.id);
      const pointerMoved = payload.dx !== 0 || payload.dy !== 0;
      if (!pointerMoved) {
        restorePageBeforeDrag();
        return;
      }

      const pagesNow = pagesRef.current;
      const workspaceNow = workspaceElementsRef.current;
      let committedToDropPage = false;

      if (payload.source === 'page' && payload.sourcePageId) {
        const sourcePage = pagesNow.find((p) => p.id === payload.sourcePageId);
        if (!sourcePage) {
          restorePageBeforeDrag();
          return;
        }
        const sourceElements = sourcePage.elements;
        const bounds = unionBoundsFromDrag(
          payload.startPositions,
          sourceElements,
          payload.dx,
          payload.dy,
        );
        const worldBounds = bounds ? pageLocalBoundsToWorld(bounds, sourcePage) : null;
        const crossDrop =
          worldBounds != null
            ? resolvePageCrossDropTargetPage(
                workspacePageLayoutModeRef.current,
                pagesNow,
                worldBounds,
                payload.sourcePageId,
              )
            : null;

        if (
          dropPageIdAtRelease &&
          dropPageIdAtRelease !== payload.sourcePageId &&
          crossDrop === dropPageIdAtRelease
        ) {
          const targetPage = pagesNow.find((p) => p.id === dropPageIdAtRelease);
          if (targetPage) {
            const { x: sox, y: soy } = sourcePage.workspaceLayout;
            const { x: tox, y: toy } = targetPage.workspaceLayout;
            commitElementDrag({
              source: 'page',
              sourcePageId: payload.sourcePageId,
              target: 'page',
              targetPageId: dropPageIdAtRelease,
              positions: positions.map((p) => ({
                id: p.id,
                x: p.x + sox - tox,
                y: p.y + soy - toy,
              })),
            });
            selectElements(movedIds, 'page');
            committedToDropPage = true;
          }
        }
        if (
          !committedToDropPage &&
          bounds &&
          shouldMovePageElementsToWorkspace(sourcePage, bounds)
        ) {
          commitElementDrag({
            source: 'page',
            sourcePageId: payload.sourcePageId,
            target: 'workspace',
            positions: positions.map((p) => ({
              id: p.id,
              x: p.x + sourcePage.workspaceLayout.x,
              y: p.y + sourcePage.workspaceLayout.y,
            })),
          });
          selectElements(movedIds, 'workspace');
          restorePageBeforeDrag();
          return;
        }
        if (!committedToDropPage) {
          setElementPositions(positions);
          restorePageBeforeDrag();
        }
        return;
      }

      if (payload.source === 'workspace') {
        const bounds = unionBoundsFromDrag(
          payload.startPositions,
          workspaceNow,
          payload.dx,
          payload.dy,
        );
        const computedDrop = bounds
          ? resolveWorkspaceDropTargetPage(
              workspacePageLayoutModeRef.current,
              pagesNow,
              currentPageIdRef.current,
              bounds,
              draggingPageIdRef.current,
            )
          : null;
        const dropId =
          dropPageIdAtRelease && computedDrop === dropPageIdAtRelease
            ? dropPageIdAtRelease
            : null;
        if (dropId) {
          const targetPage = pagesNow.find((p) => p.id === dropId);
          if (!targetPage) {
            setWorkspaceElementPositions(positions);
            selectElements(movedIds, 'workspace');
            restorePageBeforeDrag();
            return;
          }
          commitElementDrag({
            source: 'workspace',
            target: 'page',
            targetPageId: dropId,
            positions: positions.map((p) => ({
              id: p.id,
              x: p.x - targetPage.workspaceLayout.x,
              y: p.y - targetPage.workspaceLayout.y,
            })),
          });
          selectElements(movedIds, 'page');
          if (workspacePageLayoutModeRef.current === 'single') {
            setCurrentPageId(dropId);
            bringPageToFront(dropId);
          }
          committedToDropPage = true;
        } else {
          setWorkspaceElementPositions(positions);
          selectElements(movedIds, 'workspace');
          restorePageBeforeDrag();
        }
      }
    },
    [
      commitElementDrag,
      setElementPositions,
      setWorkspaceElementPositions,
      selectElements,
      restorePageBeforeDrag,
    ],
  );

  const handleElementDragCancel = useCallback(() => {
    ownershipDropTargetCommittedRef.current = null;
    setOwnershipDropPageId(null);
    setPageDragEscapeToWorkspace(false);
    restorePageBeforeDrag();
    cancelPendingHistory();
  }, [cancelPendingHistory, restorePageBeforeDrag]);

  const { elementDrag, dragOffset, startDrag, alignmentGuides } = useElementDrag(zoom, pageSnapRef, {
    onDragMove: handleElementDragMove,
    onDragEnd: handleElementDragEnd,
    onDragCancel: handleElementDragCancel,
  });

  const snapPage =
    elementDrag?.source === 'page' && elementDrag.sourcePageId
      ? pages.find((p) => p.id === elementDrag.sourcePageId)
      : null;
  const activeSnapPage = snapPage ?? pages.find((p) => p.id === currentPageId);
  pageSnapRef.current = {
    elements: activeSnapPage?.elements ?? elements,
    blocks: activeSnapPage?.blocks ?? [],
    guides: activeSnapPage ? gridGuidesForPage(activeSnapPage) : gridGuides,
    pageWidth: activeSnapPage?.size.width ?? pageSize.width,
    pageHeight: activeSnapPage?.size.height ?? pageSize.height,
    zoom,
  };

  useEffect(() => {
    if (elementDrag && !hadElementDragRef.current) {
      currentPageIdBeforeDragRef.current = currentPageIdRef.current;
      ownershipDropTargetCommittedRef.current = null;
    }
    hadElementDragRef.current = !!elementDrag;
    if (elementDrag) return;
    ownershipDropTargetCommittedRef.current = null;
    setOwnershipDropPageId(null);
    setPageDragEscapeToWorkspace(false);
    currentPageIdBeforeDragRef.current = null;
  }, [elementDrag]);

  const hideSelectionChromeActive = hideSelectionChrome || !!elementDrag;

  // ── 组内编辑：实时边界 & 拖拽后同步 ─────────────────────────────────────────────

  /**
   * 实时计算组边界（画布绝对坐标）。
   * 拖拽过程中对选中子元素应用视觉偏移，使虚线框随子元素一起移动。
   */
  const liveEditingGroupRect = useMemo(() => {
    if (!editingGroupId) return null;
    const grp = elements.find((e) => e.id === editingGroupId) as GroupElementType | undefined;
    if (!grp || !grp.data.children?.length) return null;
    const children = grp.data.children
      .map((id) => elements.find((e) => e.id === id))
      .filter(Boolean) as import('@/components/canvas/document/publicTypes').Element[];
    if (!children.length) return null;

    // 使用画布绝对坐标（嵌套组的 grp.x/y 是父组相对坐标，需要递归求绝对值）
    const grpAbsPos = getElementAbsolutePos(editingGroupId, elements);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const child of children) {
      let cx = grpAbsPos.x + child.layout.x;
      let cy = grpAbsPos.y + child.layout.y;
      // dragOffset 是画布坐标单位（SelectionBoxLayer 直接以 el.x + dx 渲染），
      // 直接叠加，不需要除以 zoom
      if (elementDrag && selectedElementIds.includes(child.id)) {
        cx += dragOffset.dx;
        cy += dragOffset.dy;
      }
      minX = Math.min(minX, cx);
      minY = Math.min(minY, cy);
      maxX = Math.max(maxX, cx + child.size.width);
      maxY = Math.max(maxY, cy + child.size.height);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }, [editingGroupId, elements, elementDrag, dragOffset, selectedElementIds]);

  /**
   * 拖拽结束后将组的 x/y/w/h 同步到紧包围盒，并重新计算子元素相对坐标。
   * prevGroupDragRef 跟踪 elementDrag 从 truthy → falsy 的边缘。
   */
  const prevGroupDragRef = useRef<typeof elementDrag>(null);
  useEffect(() => {
    const wasDragging = !!prevGroupDragRef.current;
    prevGroupDragRef.current = elementDrag;

    if (!wasDragging || !!elementDrag || !editingGroupId) return;

    const grp = elements.find((e) => e.id === editingGroupId) as GroupElementType | undefined;
    if (!grp || !grp.data.children?.length) return;
    const children = grp.data.children
      .map((id) => elements.find((e) => e.id === id))
      .filter(Boolean) as import('@/components/canvas/document/publicTypes').Element[];
    if (!children.length) return;

    const minX = Math.min(...children.map((c) => c.layout.x));
    const minY = Math.min(...children.map((c) => c.layout.y));
    const maxX = Math.max(...children.map((c) => c.layout.x + c.size.width));
    const maxY = Math.max(...children.map((c) => c.layout.y + c.size.height));

    // 直接使用原始 updateElement，绕过坐标变换 wrapper 避免二次偏移
    const rawUpdate = updateElementRef.current;
    rawUpdate(editingGroupId, {
      layout: { x: grp.layout.x + minX, y: grp.layout.y + minY },
      size: { width: maxX - minX, height: maxY - minY },
    });
    // 组原点偏移时，同步调整所有子元素坐标到新原点相对值
    if (minX !== 0 || minY !== 0) {
      children.forEach((child) => {
        rawUpdate(child.id, { layout: { x: child.layout.x - minX, y: child.layout.y - minY } });
      });
    }
    // 同步完成后更新 editingGroupDataRef，避免本帧 resize/rotate wrapper 使用过期偏移
    editingGroupDataRef.current = { x: grp.layout.x + minX, y: grp.layout.y + minY, childIds: grp.data.children };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementDrag, editingGroupId]);

  const getMinSize: GetMinSize = useCallback((id, currentWidth, currentHeight) => {
    const el =
      elements.find((e) => e.id === id) ?? workspaceElements.find((e) => e.id === id);
    if (el?.type === 'text') {
      const textEl = el as TextElementType;
      const min = getTextMinSize(textEl, currentWidth, currentHeight);
      if (getTextLayoutFromElement(textEl).writingMode === 'vertical-rl') {
        return { minWidth: min.minWidth, minHeight: 1 };
      }
      return { minWidth: 1, minHeight: min.minHeight };
    }
    if (el?.type === 'table') {
      const table = el as TableElementType;
      const rows = Math.max(1, table.data.rows);
      const cols = Math.max(1, table.data.cols);
      // 表格「可压缩最小」= 行数×最小行高、列数×最小列宽（与是否有数据无关；store 会用 distributeWithMin 重算 rowHeights/colWidths）
      // 若用 sum(rowHeights)/sum(colWidths) 作为 min，则最小=当前尺寸，无法再缩小，角柄会「无法缩放」
      const minWidth = cols * TABLE_MIN_COL_WIDTH;
      const minHeight = rows * TABLE_MIN_ROW_HEIGHT;
      return { minWidth, minHeight };
    }
    return { minWidth: 1, minHeight: 1 };
  }, [elements, workspaceElements]);

  const { resizeState, setResizeState, resizePreview, setResizePreview, resizePreviewRef } = useResize(zoom, updateElementSilentWrapped, updateElementWrapped, undefined, getMinSize);
  const { groupResizeState, setGroupResizeState, groupResizePreview, setGroupResizePreview, groupResizePreviewRef } = useGroupResize(zoom, updateElementSilentWrapped, commitGroupResize);
  const {
    multiResizeState,
    setMultiResizeState,
    multiResizePreview,
    multiResizePreviewRef,
  } = useMultiSelectionResize(
    zoom,
    pages,
    updateElementSilentInDocument,
    commitMultiSelectionResize,
  );

  const selectedWorkspaceCount = selectedElementIds.filter((id) =>
    workspaceElements.some((w) => w.id === id),
  ).length;
  const useWorldSelectionChrome =
    selectedPageIds.length > 0 ||
    (selectionScope === 'workspace' && selectedWorkspaceCount >= 2);

  const pageUnionRect = useMemo(() => {
    if (useWorldSelectionChrome || selectedElementIds.length < 2) return null;
    const selected = rootElements.filter(
      (el) => selectedElementIds.includes(el.id) && el.type !== 'line' && el.id !== editingGroupId,
    );
    const offset = elementDrag ? dragOffset : { dx: 0, dy: 0 };
    return unionBoundsFromPageElements(selected, offset);
  }, [
    useWorldSelectionChrome,
    selectedElementIds,
    rootElements,
    editingGroupId,
    elementDrag,
    dragOffset,
  ]);

  const worldUnionRect = useMemo(() => {
    if (!useWorldSelectionChrome) return null;
    const elementUnion = unionBoundsFromSnapshots(
      buildDocumentSnapshots(pages, workspaceElements, selectedElementIds),
      pages,
    );
    const pageRects = selectedPageIds
      .map((id) => pages.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => pageSurfaceWorldBounds(p!));
    if (!elementUnion && pageRects.length === 0) return null;
    if (!elementUnion) return unionBoundsFromRects(pageRects);
    if (pageRects.length === 0) return elementUnion;
    return unionBoundsFromRects([elementUnion, ...pageRects]);
  }, [useWorldSelectionChrome, pages, workspaceElements, selectedElementIds, selectedPageIds]);

  const startUnionResize = useCallback(
    (handle: ResizeHandle, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      pushHistory();
      const union =
        useWorldSelectionChrome
          ? worldUnionRect
          : pageUnionRect;
      if (!union) return;
      const snapshots = buildDocumentSnapshots(pages, workspaceElements, selectedElementIds);
      if (snapshots.length === 0) return;
      setMultiResizeState({
        handle,
        startX: union.x,
        startY: union.y,
        startW: union.width,
        startH: union.height,
        startClientX: e.clientX,
        startClientY: e.clientY,
        snapshots,
        pages,
        coordinateSpace: useWorldSelectionChrome ? 'world' : 'page',
      });
    },
    [
      pushHistory,
      useWorldSelectionChrome,
      worldUnionRect,
      pageUnionRect,
      pages,
      workspaceElements,
      selectedElementIds,
      setMultiResizeState,
    ],
  );
  const pageToCanvas = useCallback(
    (clientX: number, clientY: number) => {
      const pageEl = pageRef.current;
      if (!pageEl) return { x: 0, y: 0 };
      const r = pageEl.getBoundingClientRect();
      return { x: (clientX - r.left) / zoom, y: (clientY - r.top) / zoom };
    },
    [pageRef, zoom],
  );
  const worldToCanvas = useCallback(
    (clientX: number, clientY: number) => {
      const host = stageHostRef.current;
      if (!host) return { x: 0, y: 0 };
      return screenToWorld(clientX, clientY, host.getBoundingClientRect(), panX, panY, zoom);
    },
    [stageHostRef, panX, panY, zoom],
  );
  const { lineHandleState, setLineHandleState, lineHandlePreview, setLineHandlePreview } = useLineHandle(
    pageToCanvas,
    worldToCanvas,
    updateElementSilentWrapped,
    updateElementWrapped,
  );
  const { radiusDragState, setRadiusDragState, radiusPreview, setRadiusPreview, radiusPreviewRef } = useRadiusDrag(
    pageRef,
    zoom,
    updateElementSilentWrapped,
    updateElementWrapped,
  );
  const { rotateState, setRotateState, rotatePreview, setRotatePreview, rotatePreviewRef } = useRotate(updateElementSilentWrapped, updateElementWrapped);

  const [contextMenuTarget, setContextMenuTarget] = useState<CanvasContextMenuTarget | null>(null);
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);

  const selectedSet = useRef(new Set<string>());
  selectedSet.current = new Set(selectedElementIds);

  // 用 ref 保存最新的 editingTextId，避免 onDismiss 闭包中读到旧值
  const editingTextIdRef = useRef<string | null>(editingTextId);
  editingTextIdRef.current = editingTextId;

  /** 文本在 mousedown 进入编辑后节点会卸载，紧随的 click 可能落在画布上误触退出编辑 */
  const suppressNextCanvasClickRef = useRef(false);

  const editingGroupIdRef = useRef(editingGroupId);
  editingGroupIdRef.current = editingGroupId;

  const exitGroupEditMode = useCallback(() => {
    const gid = editingGroupIdRef.current;
    if (!gid) return;
    const parentGrp = elementsRef.current.find(
      (e) =>
        e.type === 'group' &&
          ((e as GroupElementType).data.children ?? []).includes(gid),
    ) as GroupElementType | undefined;
    if (parentGrp) {
      setEditingGroupId(parentGrp.id);
      clearSelection();
      selectElement(gid, false);
    } else {
      setEditingGroupId(null);
      clearSelection();
      selectElement(gid, false);
    }
  }, [setEditingGroupId, clearSelection, selectElement]);

  const beginTextEdit = useCallback(
    (id: string, opts?: import('@/components/canvas/elements/types').TextEditOptions) => {
      if (isReadonlyMode) return;
      textEditingState.pendingClickX = opts?.clickX ?? null;
      textEditingState.pendingClickY = opts?.clickY ?? null;
      textEditingState.pendingSelectAll = opts?.selectAll ?? false;
      suppressNextCanvasClickRef.current = true;
      if (workspaceElements.some((w) => w.id === id)) {
        selectElements([id], 'workspace');
      } else {
        selectElement(id, false);
      }
      setEditingTextId(id);
    },
    [isReadonlyMode, workspaceElements, selectElements, selectElement, setEditingTextId],
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isReadonlyMode) return;
      if (suppressNextCanvasClickRef.current) {
        suppressNextCanvasClickRef.current = false;
        return;
      }
      const target = e.target as HTMLElement;
      if (
        target.closest?.('[data-element-hit]') ||
        target.closest?.('[data-block-hit]') ||
        target.closest?.('[data-handle]')
      ) {
        return;
      }
      if (isWithinTextEditChrome(target)) return;

      // 若处于组内编辑模式，点击组边界外 → 退出
      if (editingGroupIdRef.current) {
        const inGroup = target.closest?.('[data-group-editing-id]');
        if (!inGroup) {
          exitGroupEditMode();
          return;
        }
        // 点击组背景（非子元素区域）→ 仅清除子元素选中
        clearSelection();
        setEditingTextId(null);
        return;
      }

      selectPageFrame();
      setEditingTextId(null);
    },
    [isReadonlyMode, selectPageFrame, exitGroupEditMode, clearSelection, setEditingTextId],
  );

  const handleElementHitMouseDown = useCallback(
    (e: React.MouseEvent, el: Element) => {
      if (isReadonlyMode) {
        e.stopPropagation();
        return;
      }
      if (e.button !== 0) {
        e.stopPropagation();
        return;
      }
      // 若当前在组内编辑状态，点击组外元素 → 先退出（或回到父组）
      if (editingGroupIdRef.current) {
        const gid = editingGroupIdRef.current;
        const grp = elementsRef.current.find((g) => g.id === gid) as
          | import('@/components/canvas/document/publicTypes').GroupElement
          | undefined;
        if (grp && !grp.data.children.includes(el.id) && el.id !== gid) {
          // 点击了编辑组以外的元素：寻找父组
          const parentGrp = elementsRef.current.find(
            (e) =>
              e.type === 'group' &&
              ((e as import('@/components/canvas/document/publicTypes').GroupElement).data.children ?? []).includes(gid),
          ) as import('@/components/canvas/document/publicTypes').GroupElement | undefined;
          if (parentGrp?.data.children.includes(el.id)) {
            // 点击了父组内的兄弟元素 → 切换到父组编辑状态
            setEditingGroupId(parentGrp.id);
            clearSelection();
            // 继续执行下方的选中 / 拖拽逻辑
          } else {
            // 完全在组层级之外 → 退出所有组编辑
            setEditingGroupId(null);
            clearSelection();
          }
        }
      }
      if (isPanModeActive) return;
      if (el.interaction.locked) return;
      e.preventDefault();
      e.stopPropagation();

      if (selectedSet.current.has(el.id)) {
        // ── 已选中 ──────────────────────────────────────────────────────────────
        if (e.shiftKey && selectedSet.current.size > 1) {
          // Shift+click 已选中元素：取消选中（不启动拖拽）
          const newIds = [...selectedSet.current].filter((id) => id !== el.id);
          selectedSet.current = new Set(newIds);
          selectElements(newIds);
          return;
        }
        // 正常拖拽全部选中元素
        pushHistory();
        const startPositions = elements
          .filter((elem) => selectedSet.current.has(elem.id) && !elem.interaction.locked)
          .map((elem) => ({ id: elem.id, x: elem.layout.x, y: elem.layout.y }));
        startDrag(e.clientX, e.clientY, startPositions, {
          source: 'page',
          sourcePageId: currentPageId,
        });
      } else if (e.shiftKey) {
        // ── Shift+click 未选中元素：追加到选中集并开始拖拽 ──────────────────────
        pushHistory();
        const newSet = new Set([...selectedSet.current, el.id]);
        selectedSet.current = newSet;
        selectElement(el.id, true); // addToSelection = true
        const startPositions = elements
          .filter((elem) => newSet.has(elem.id) && !elem.interaction.locked)
          .map((elem) => ({ id: elem.id, x: elem.layout.x, y: elem.layout.y }));
        startDrag(e.clientX, e.clientY, startPositions, {
          source: 'page',
          sourcePageId: currentPageId,
        });
      } else {
        // ── 普通点击未选中元素：单选并拖拽 ─────────────────────────────────────
        pushHistory();
        selectedSet.current = new Set([el.id]);
        selectElement(el.id, false);
        startDrag(e.clientX, e.clientY, [{ id: el.id, x: el.layout.x, y: el.layout.y }], {
          source: 'page',
          sourcePageId: currentPageId,
        });
      }
    },
    [isReadonlyMode, elements, isPanModeActive, pushHistory, startDrag, selectElement, selectElements,
     setEditingGroupId, clearSelection, currentPageId]
  );

  const beginWorkspaceElementDrag = useCallback(
    (clientX: number, clientY: number, startPositions: { id: string; x: number; y: number }[]) => {
      cancelPendingHistory();
      pushHistory();
      startDrag(clientX, clientY, startPositions, { source: 'workspace' });
    },
    [cancelPendingHistory, pushHistory, startDrag],
  );

  const handleWorkspaceElementMouseDown = useCallback(
    (e: React.MouseEvent, el: Element) => {
      if (isReadonlyMode) {
        e.stopPropagation();
        return;
      }
      if (e.button !== 0) {
        e.stopPropagation();
        return;
      }
      if (isPanModeActive) return;
      if (el.interaction.locked) return;
      e.preventDefault();
      e.stopPropagation();

      const wsSelected = selectedElementIds.filter((id) =>
        workspaceElements.some((w) => w.id === id),
      );
      const selectedSetWs = new Set(wsSelected.length > 0 ? wsSelected : []);

      if (selectedSetWs.has(el.id)) {
        if (e.shiftKey && selectedSetWs.size > 1) {
          const newIds = [...selectedSetWs].filter((id) => id !== el.id);
          selectElements(newIds, 'workspace');
          return;
        }
        const startPositions = workspaceElements
          .filter((elem) => selectedSetWs.has(elem.id) && !elem.interaction.locked)
          .map((elem) => ({ id: elem.id, x: elem.layout.x, y: elem.layout.y }));
        beginWorkspaceElementDrag(e.clientX, e.clientY, startPositions);
      } else if (e.shiftKey) {
        const newIds = [...selectedSetWs, el.id];
        selectElements(newIds, 'workspace');
        const startPositions = workspaceElements
          .filter((elem) => newIds.includes(elem.id) && !elem.interaction.locked)
          .map((elem) => ({ id: elem.id, x: elem.layout.x, y: elem.layout.y }));
        beginWorkspaceElementDrag(e.clientX, e.clientY, startPositions);
      } else {
        selectElements([el.id], 'workspace');
        beginWorkspaceElementDrag(e.clientX, e.clientY, [
          { id: el.id, x: el.layout.x, y: el.layout.y },
        ]);
      }
    },
    [
      isReadonlyMode,
      isPanModeActive,
      pushHistory,
      workspaceElements,
      selectedElementIds,
      selectElements,
      beginWorkspaceElementDrag,
    ],
  );

  const workspaceDragPreviewOnPage = useMemo(() => {
    const pointerMoved =
      elementDrag != null &&
      (dragOffset.dx !== 0 || dragOffset.dy !== 0);
    if (!elementDrag || !ownershipDropPageId || !pointerMoved) {
      return null;
    }
    const targetPage = pages.find((p) => p.id === ownershipDropPageId);
    if (!targetPage) return null;
    const { x: tox, y: toy } = targetPage.workspaceLayout;

    if (elementDrag.source === 'workspace') {
      const previewElements = elementDrag.startPositions
        .map(({ id, x, y }) => {
          const el = workspaceElements.find((e) => e.id === id);
          if (!el) return null;
          return {
            ...el,
            layout: {
              ...el.layout,
              x: Math.round(x + dragOffset.dx - tox),
              y: Math.round(y + dragOffset.dy - toy),
            },
          };
        })
        .filter(Boolean) as Element[];
      return { pageId: ownershipDropPageId, elements: previewElements };
    }

    if (
      elementDrag.source === 'page' &&
      elementDrag.sourcePageId &&
      elementDrag.sourcePageId !== ownershipDropPageId
    ) {
      const sourcePage = pages.find((p) => p.id === elementDrag.sourcePageId);
      if (!sourcePage) return null;
      const { x: sox, y: soy } = sourcePage.workspaceLayout;
      const previewElements = elementDrag.startPositions
        .map(({ id, x, y }) => {
          const el = sourcePage.elements.find((e) => e.id === id);
          if (!el) return null;
          return {
            ...el,
            layout: {
              ...el.layout,
              x: Math.round(x + dragOffset.dx + sox - tox),
              y: Math.round(y + dragOffset.dy + soy - toy),
            },
          };
        })
        .filter(Boolean) as Element[];
      return previewElements.length > 0
        ? { pageId: ownershipDropPageId, elements: previewElements }
        : null;
    }

    return null;
  }, [elementDrag, ownershipDropPageId, pages, workspaceElements, dragOffset]);

  const pageDragPreviewOnWorkspace = useMemo(() => {
    if (
      !elementDrag ||
      elementDrag.source !== 'page' ||
      !pageDragEscapeToWorkspace ||
      !elementDrag.sourcePageId
    ) {
      return null;
    }
    const page = pages.find((p) => p.id === elementDrag.sourcePageId);
    if (!page) return null;
    const { x: ox, y: oy } = page.workspaceLayout;
    const previewElements = elementDrag.startPositions
      .map(({ id, x, y }) => {
        const el = elements.find((e) => e.id === id);
        if (!el) return null;
        return {
          ...el,
          layout: {
            ...el.layout,
            x: Math.round(x + dragOffset.dx + ox),
            y: Math.round(y + dragOffset.dy + oy),
          },
        };
      })
      .filter(Boolean) as Element[];
    return previewElements.length > 0 ? previewElements : null;
  }, [elementDrag, pageDragEscapeToWorkspace, pages, elements, dragOffset]);

  const handleCanvasDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isReadonlyMode) {
        e.stopPropagation();
        return;
      }
      if (background.type !== 'image' || !background.value) {
        // ── 组内编辑模式：在编辑组边界内双击 ──
        const groupBoundary = (e.target as HTMLElement).closest('[data-group-editing-id]');
        if (groupBoundary) {
          // 双击子元素进入文本编辑或嵌套组编辑
          const childHit = (e.target as HTMLElement).closest('[data-element-hit]');
          if (childHit) {
            const cid = childHit.getAttribute('data-element-id');
            const child = cid ? elements.find((elem) => elem.id === cid) : null;
            if (child?.type === 'text') {
              e.preventDefault();
              beginTextEdit(child.id);
            } else if (child?.type === 'group') {
              // 双击嵌套组 → 进入该嵌套组的编辑模式
              e.preventDefault();
              setEditingGroupId(child.id);
              clearSelection();
            }
          }
          return;
        }

        const hit = (e.target as HTMLElement).closest('[data-element-hit]') as HTMLElement | null;
        if (hit) {
          const id = hit.getAttribute('data-element-id');
          let el = id ? visibleElements.find((elem) => elem.id === id) : null;

          // 若命中的是组内子元素（e.g. 鼠标在透明遮罩上时不会出现此情况，
          // 但在组编辑模式切换过程中可能出现），向上寻找父组
          if (el && !rootElements.some((r) => r.id === el!.id)) {
            const parentHit = hit.parentElement?.closest('[data-element-hit]') as HTMLElement | null;
            const parentId = parentHit?.getAttribute('data-element-id');
            const parentEl = parentId ? visibleElements.find((elem) => elem.id === parentId) : null;
            if (parentEl?.type === 'group') el = parentEl;
          }

          // ── 双击组 → 进入组内编辑 ──
          if (el?.type === 'group') {
            e.preventDefault();
            setEditingGroupId(el.id);
            clearSelection();
            return;
          }
          if (el?.type === 'text') {
            e.preventDefault();
            beginTextEdit(el.id);
          }
        }
        return;
      }
      if ((e.target as HTMLElement).closest('[data-element-hit]')) return;
      e.preventDefault();
      e.stopPropagation();
      startBgImageDrag(e);
    },
    [isReadonlyMode, background.type, background.value, elements, visibleElements, beginTextEdit,
     setEditingGroupId, clearSelection, startBgImageDrag]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isReadonlyMode) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (isPanModeActive) return;
      const target = e.target as HTMLElement;
      const hit = target.closest('[data-element-hit]');
      const pageEl = target.closest('[data-page]') as HTMLElement | null;
      const pageId = pageEl?.getAttribute('data-page') ?? '';
      const pageRect = pageEl?.getBoundingClientRect();
      const pageTarget: CanvasContextMenuTarget | null = pageEl && pageRect && pageId
        ? {
            type: 'page',
            pageId,
            point: {
              x: (e.clientX - pageRect.left) / zoom,
              y: (e.clientY - pageRect.top) / zoom,
            },
          }
        : null;
      const workspacePoint = worldToCanvas(e.clientX, e.clientY);
      let nextTarget: CanvasContextMenuTarget;
      if (hit) {
        const id = hit.getAttribute('data-element-id');
        const el = id
          ? visibleElements.find((elem) => elem.id === id) ??
            workspaceElements.find((elem) => elem.id === id && elem.interaction.visible !== false)
          : null;
        if (el) {
          const isWorkspaceElement = workspaceElements.some((elem) => elem.id === el.id);
          const hitInSelection = isHitInCurrentSelection(
            el.id,
            el.layout.parentId,
            selectedSet.current,
            elements,
          );
          if (!hitInSelection && selectedSet.current.size < 2) {
            if (isWorkspaceElement) selectElements([el.id], 'workspace');
            else selectElement(el.id, false);
          }
          nextTarget = { type: 'element', element: el };
        } else {
          nextTarget = pageTarget ?? { type: 'workspace', point: workspacePoint };
        }
      } else if (pageTarget) {
        nextTarget = pageTarget;
      } else {
        nextTarget = { type: 'workspace', point: workspacePoint };
      }
      flushSync(() => setContextMenuTarget(nextTarget));
    },
    [isReadonlyMode, isPanModeActive, visibleElements, workspaceElements, elements, selectElements, selectElement, worldToCanvas, zoom]
  );

  const handleMouseUpCombined = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0 && editingImagePosition) {
        setEditingImagePosition(null);
        return;
      }
      handleViewportMouseUp(e);
      if (e.button === 0 && isMovingBgImage) {
        e.preventDefault();
        setIsMovingBgImage(false);
      }
    },
    [handleViewportMouseUp, isMovingBgImage, setIsMovingBgImage, editingImagePosition]
  );

  const handleStartImagePositionDrag = useCallback(
    (el: ImageElementType, e: React.MouseEvent) => {
      const x = el.data.objectPosition?.x ?? 50;
      const y = el.data.objectPosition?.y ?? 50;
      setEditingImagePosition({
        id: el.id,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startObjectPositionX: x,
        startObjectPositionY: y,
      });
    },
    []
  );

  const baseCursor = editingImagePosition
    ? 'move'
    : isMovingBgImage
      ? 'move'
      : isMarqueeActive
        ? 'crosshair'
        : effectiveTextDrawMode || effectiveShapeDrawMode || effectiveTableDrawMode
          ? 'crosshair'
          : (panCursor ?? 'default');

  useEffect(() => {
    const host = stageHostRef.current;
    if (!host) return;
    host.style.cursor = baseCursor;
  }, [baseCursor, stageHostRef]);

  useEffect(() => {
    const host = stageHostRef.current;
    if (!host || !editingImagePosition) return;
    const onWheel = (e: WheelEvent) => {
      const el = elements.find((elem) => elem.id === editingImagePosition.id) as ImageElementType | undefined;
      if (!el || el.type !== 'image') return;
      e.preventDefault();
      const current = el.data.imageScale ?? 1;
      const delta = -e.deltaY * 0.002;
      const next = Math.min(5, Math.max(0.1, current + delta));
      if (next !== current) updateElement(el.id, { data: { imageScale: next } });
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  }, [editingImagePosition, elements, updateElement, stageHostRef]);

  const getPageForResize = useCallback(() => getPage(), [getPage]);

  const applyPreview = useCallback(
    (preview: PageResizePreview, elementStarts: { id: string; x: number; y: number }[], localOffset: { x: number; y: number }) => {
      applyPageFrameResizeSilent({
        width: preview.width,
        height: preview.height,
        workspaceX: preview.workspaceX,
        workspaceY: preview.workspaceY,
        localOffsetX: localOffset.x,
        localOffsetY: localOffset.y,
        elementStarts,
      });
    },
    [applyPageFrameResizeSilent],
  );

  const commitPreview = useCallback(
    (preview: PageResizePreview, elementStarts: { id: string; x: number; y: number }[], localOffset: { x: number; y: number }) => {
      commitPageFrameResize({
        width: preview.width,
        height: preview.height,
        workspaceX: preview.workspaceX,
        workspaceY: preview.workspaceY,
        localOffsetX: localOffset.x,
        localOffsetY: localOffset.y,
        elementStarts,
      });
    },
    [commitPageFrameResize],
  );

  const { pageResizePreview, startPageResize } = usePageResize(
    zoom,
    getPageForResize,
    applyPreview,
    commitPreview,
    pushHistory,
  );

  const handleContentBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      if (suppressNextCanvasClickRef.current) {
        suppressNextCanvasClickRef.current = false;
        return;
      }
      const target = e.target as HTMLElement;
      if (isWithinTextEditChrome(target)) return;
      if (target.closest('[data-page]')) return;
      if (target.closest('[data-page-chrome]')) return;
      if (target.closest('[data-handle]')) return;
      if (target.closest('[data-element-hit]') || target.closest('[data-block-hit]')) return;
      deactivateCurrentPage();
    },
    [deactivateCurrentPage],
  );

  return {
    elements,
    visibleElements,
    rootElements,
    elementDrag,
    dragOffset,
    isPresentationMode,
    isReadonlyMode,
    liveEditingGroupRect,
    hoveredElementId,
    setHoveredElementId,
    handleCanvasClick,
    handleCanvasDoubleClick,
    handleCanvasMouseDown,
    handleElementHitMouseDown,
    handleWorkspaceElementMouseDown,
    handleContextMenu,
    contextMenuTarget,
    closeContextMenu: () => setContextMenuTarget(null),
    handleMouseUpCombined,
    handleContentBackgroundClick,
    handleStartImagePositionDrag,
    beginTextEdit,
    exitGroupEditMode,
    editingTextIdRef,
    selectElement,
    resizeState,
    setResizeState,
    resizePreview,
    setResizePreview,
    resizePreviewRef,
    groupResizeState,
    setGroupResizeState,
    groupResizePreview,
    setGroupResizePreview,
    groupResizePreviewRef,
    lineHandleState,
    setLineHandleState,
    lineHandlePreview,
    setLineHandlePreview,
    radiusDragState,
    setRadiusDragState,
    radiusPreview,
    setRadiusPreview,
    radiusPreviewRef,
    rotateState,
    setRotateState,
    rotatePreview,
    setRotatePreview,
    rotatePreviewRef,
    updateElement,
    setEditingTextId,
    pushHistory,
    penToolActive,
    freehandToolActive,
    eraserToolActive,
    selectedElementIds,
    editingGroupId,
    editingTextId,
    editingTableCellId,
    selectedTableColumn,
    selectedTableRow,
    selectedTableCells,
    isMarqueeActive,
    marquee,
    isTextDrawing,
    textDrawRect,
    isShapeDrawing,
    shapeDrawPreview,
    isTableDrawing,
    tableDrawRect,
    tableDrawPending,
    setTableDrawRows,
    setTableDrawCols,
    confirmTableDraw,
    cancelTableDraw,
    baseCursor,
    editingImagePosition,
    setEditingImagePosition,
    isPageSelected,
    pageResizePreview,
    startPageResize,
    onPageFrameDragStart,
    pageSize,
    useWorldSelectionChrome,
    pageUnionRect,
    worldUnionRect,
    multiResizePreview,
    startUnionResize,
    alignmentGuides,
    pageSnapRef,
    ownershipDropPageId,
    workspaceDragPreviewOnPage,
    pageDragEscapeToWorkspace,
    pageDragPreviewOnWorkspace,
    hideSelectionChrome: hideSelectionChromeActive,
    selectionScope,
    selectedPageIds,
    pages,
    workspaceElements,
    currentPageId,
    zoom,
  };
}
